/**
 * ADM-01 运营首页：按原型 admin/src/pages/HomePage.tsx 逐区块转译。
 *
 * 操作仅「切换校区、切换统计日期、刷新」三项；指标卡/今日课次/待办/逾期
 * Alert 均不可点击跳转（PRD ADM-000 裁决）；统计日期只过滤今日课次；
 * 失败段计数显示「—」而非 0（sectionErrors 分段降级）。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, List, Space, Tag, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useGlobalCampusScope } from '../../auth/GlobalCampusContext';
import { useSession } from '../../auth/SessionContext';
import { operationsApi, type OperationsDashboard } from '../../api/operations.api';
import { ApiError } from '../../api/transport';

const { Title, Text } = Typography;

/** 星期标签（原型 weekdayLabel，周日映射 7）。 */
const WEEKDAY_LABEL: Record<number, string> = {
  1: '周一', 2: '周二', 3: '周三', 4: '周四', 5: '周五', 6: '周六', 7: '周日',
};

/** 课次状态标签（对齐原型 LessonSessionStatusTag）。 */
function SessionStatusTag({ status }: { status: string }) {
  if (status === 'SCHEDULED') return <Tag color="processing">待上课</Tag>;
  if (status === 'COMPLETED') return <Tag color="success">已完成</Tag>;
  return <Tag>已取消</Tag>;
}

/** 指标卡：失败段显示「—」，正数按 tone 着色（对齐原型 StatCard）。 */
function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | null;
  tone?: 'warning' | 'error';
}) {
  const color =
    tone === 'error' ? 'var(--ant-color-error)' : tone === 'warning' ? 'var(--ant-color-warning)' : undefined;
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div
        className="stat-value"
        style={{ color: value === null ? 'var(--ant-color-text-tertiary)' : color }}
      >
        {value === null ? '—' : value}
      </div>
    </div>
  );
}

const EMPTY_DASHBOARD: OperationsDashboard = {
  campusId: null,
  statDate: '',
  enrollmentCount: 0,
  trialingCount: 0,
  paidOrderCount: 0,
  pendingPlacementCount: 0,
  inClassCount: 0,
  pendingDepositCount: 0,
  pendingFullCount: 0,
  pendingFinalCount: 0,
  overdueFinalCount: 0,
  undeterminedCount: 0,
  todaySessionCount: 0,
  todaySessions: [],
  pendingDeductionCount: 0,
  deductionExceptionCount: 0,
  fundExceptionCount: 0,
  todos: [],
};

/** 计数兜底：失败段 null 之外显示原值。 */
function count(value: number | null): number | null {
  return value;
}

/** 正数告警 tone（对齐原型：>0 才着色）。 */
function positiveTone(value: number | null, tone: 'warning' | 'error') {
  return value !== null && value > 0 ? tone : undefined;
}

/** ADM-01 运营首页。 */
export function OperationsDashboardPage() {
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [dashboard, setDashboard] = useState<OperationsDashboard>(EMPTY_DASHBOARD);
  const [error, setError] = useState<string | null>(null);
  // 全局校区范围：'ALL' 不携带 campusId（锁定模式，页内无校区筛选）；校区名取自共享上下文
  const { scope, campuses } = useGlobalCampusScope();
  // 校区锁定账号（非总部）无切换器、scope 恒 'ALL'，页头须展示授权校区名而非「全部校区」
  const isHq = useSession().permissions?.hasAllOrgScope === true;
  const activeCampusId = scope === 'ALL' ? undefined : scope;

  const load = useCallback(async () => {
    setError(null);
    try {
      setDashboard(
        await operationsApi.getOperationsDashboard({
          campusId: activeCampusId,
          statDate: date.format('YYYY-MM-DD'),
        }),
      );
    } catch (err) {
      setDashboard(EMPTY_DASHBOARD);
      setError(err instanceof ApiError ? err.message : '运营首页加载失败');
    }
  }, [activeCampusId, date]);

  useEffect(() => {
    void load();
  }, [load]);

  const campusName = useMemo(() => {
    if (scope !== 'ALL') return campuses.find((c) => c.id === scope)?.name ?? scope;
    if (isHq) return '全部校区';
    // 校区锁定账号：scope 恒 'ALL'，展示授权校区名（列表由后端按 campusScope 过滤，多授权校区顿号连接）
    return campuses.length > 0 ? campuses.map((c) => c.name).join('、') : '授权校区';
  }, [scope, campuses, isHq]);

  const sessions = dashboard.todaySessions ?? [];
  const todos = dashboard.todos;

  return (
    <div>
      <div className="page-header-row">
        <div>
          <Title level={3}>运营首页</Title>
          <p className="page-header-desc">
            当前校区：{campusName}，展示报名到在班的主链路概况、今日课次和待办异常。业务操作请通过左侧导航进入对应页面。
          </p>
        </div>
        <Space>
          <DatePicker value={date} onChange={(v) => v && setDate(v)} />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              void load();
              message.success('已刷新');
            }}
          >
            刷新
          </Button>
        </Space>
      </div>

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      <div className="stat-grid">
        <StatCard label="报名数" value={count(dashboard.enrollmentCount)} />
        <StatCard label="试课中人数" value={count(dashboard.trialingCount)} />
        <StatCard label="缴费成功订单" value={count(dashboard.paidOrderCount)} />
        <StatCard label="待分班人数" value={count(dashboard.pendingPlacementCount)} />
        <StatCard label="在班人数" value={count(dashboard.inClassCount)} />
        <StatCard label="待收首付款" value={count(dashboard.pendingDepositCount)} />
        <StatCard label="待收全款" value={count(dashboard.pendingFullCount)} />
        <StatCard
          label="待收尾款"
          value={count(dashboard.pendingFinalCount)}
          tone={positiveTone(dashboard.pendingFinalCount, 'warning')}
        />
        <StatCard
          label="逾期尾款"
          value={count(dashboard.overdueFinalCount)}
          tone={positiveTone(dashboard.overdueFinalCount, 'error')}
        />
        <StatCard
          label="待确定款项"
          value={count(dashboard.undeterminedCount)}
          tone={positiveTone(dashboard.undeterminedCount, 'warning')}
        />
        <StatCard label="今日课次数" value={count(dashboard.todaySessionCount)} />
        <StatCard
          label="待消课数"
          value={count(dashboard.pendingDeductionCount)}
          tone={positiveTone(dashboard.pendingDeductionCount, 'warning')}
        />
        <StatCard
          label="消课异常数"
          value={count(dashboard.deductionExceptionCount)}
          tone={positiveTone(dashboard.deductionExceptionCount, 'error')}
        />
        <StatCard
          label="资金异常数"
          value={count(dashboard.fundExceptionCount)}
          tone={positiveTone(dashboard.fundExceptionCount, 'error')}
        />
      </div>

      {dashboard.overdueFinalCount !== null && dashboard.overdueFinalCount > 0 && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message={`当前有 ${dashboard.overdueFinalCount} 笔尾款已逾期，建议前往「收费管理 - 订单收款」筛选逾期节点并催缴`}
        />
      )}

      <div className="page-content-card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          {`今日课次（${date.format('YYYY-MM-DD')} ${
            WEEKDAY_LABEL[date.day() === 0 ? 7 : date.day()]
          }）`}
        </div>
        <List
          locale={{ emptyText: '今日暂无排课' }}
          dataSource={sessions}
          renderItem={(session) => (
            <List.Item>
              <Space size={16} wrap>
                <Text strong>{dayjs(session.startAt).format('HH:mm')}</Text>
                <Text>{session.className}</Text>
                <Text type="secondary">{session.teacherName}</Text>
                <Text type="secondary">{session.classroomName}</Text>
                <Text type="secondary">{`${session.studentCount} 人`}</Text>
                <SessionStatusTag status={session.status} />
                {session.classroomChanged && <Tag color="orange">教室已调整</Tag>}
              </Space>
            </List.Item>
          )}
        />
      </div>

      <div className="page-content-card">
        <div style={{ fontWeight: 600, marginBottom: 8 }}>待办与异常</div>
        <List
          dataSource={todos}
          renderItem={(item) => (
            <List.Item>
              <Text>
                {item.type === 'PENDING_DEDUCTION'
                  ? '待消课课次'
                  : item.type === 'DEDUCTION_EXCEPTION'
                    ? '消课异常待处理'
                    : item.type === 'FUND_EXCEPTION'
                      ? '资金异常待处理'
                      : '尾款逾期'}
              </Text>
              <Tag color={item.count !== null && item.count > 0 ? 'gold' : 'default'}>
                {item.count === null ? '—' : item.count}
              </Tag>
            </List.Item>
          )}
        />
      </div>
    </div>
  );
}
